import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertDialog,
  Badge,
  Button,
  Flex,
  Heading,
  IconButton,
  Table,
  Text,
  TextField,
  Tooltip
} from '@radix-ui/themes'
import { MagnifyingGlassIcon, Pencil1Icon, PlusIcon, TrashIcon } from '@radix-ui/react-icons'
import type { TRPCOutputs } from '~/utils/trpc'
import { useTRPC } from '~/utils/trpc'
import { ContactFormDialog, type ContactSummary } from '~/features/contacts/components/ContactFormDialog'

type ContactListItem = TRPCOutputs['ops']['contacts']['list'][number]

const matchesSearch = (contact: ContactListItem, search: string): boolean => {
  const needle = search.trim().toLowerCase()
  if (!needle) return true
  return [contact.name, contact.email, contact.phone, contact.address, contact.org?.name]
    .filter((field): field is string => !!field)
    .some((field) => field.toLowerCase().includes(needle))
}

const DeleteContactButton = ({ contact }: { contact: ContactListItem }) => {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)

  const deleteMutation = useMutation(
    trpc.ops.contacts.delete.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: trpc.ops.contacts.list.queryKey() })
        setOpen(false)
      }
    })
  )

  const inUse = contact.installationCount > 0

  return (
    <AlertDialog.Root
      open={open}
      onOpenChange={(next) => {
        if (next) deleteMutation.reset()
        setOpen(next)
      }}
    >
      <AlertDialog.Trigger>
        <Tooltip content={inUse ? 'Used by installations — reassign them first' : 'Delete contact'}>
          <IconButton variant="soft" color="red" size="1" disabled={inUse}>
            <TrashIcon />
          </IconButton>
        </Tooltip>
      </AlertDialog.Trigger>
      <AlertDialog.Content maxWidth="420px">
        <AlertDialog.Title>Delete {contact.name}?</AlertDialog.Title>
        <AlertDialog.Description size="2">This cannot be undone.</AlertDialog.Description>
        {deleteMutation.error ? (
          <Text color="red" size="2" as="p" mt="3">
            {deleteMutation.error.message}
          </Text>
        ) : null}
        <Flex gap="3" mt="4" justify="end">
          <AlertDialog.Cancel>
            <Button variant="soft" color="gray" disabled={deleteMutation.isPending}>
              Cancel
            </Button>
          </AlertDialog.Cancel>
          <Button
            color="red"
            loading={deleteMutation.isPending}
            onClick={() => deleteMutation.mutate({ contactId: contact.id })}
          >
            Delete
          </Button>
        </Flex>
      </AlertDialog.Content>
    </AlertDialog.Root>
  )
}

const ContactsPage = () => {
  const trpc = useTRPC()
  const [search, setSearch] = useState('')
  const [dialog, setDialog] = useState<{ open: boolean; orgId?: string; contact: ContactSummary | null }>({
    open: false,
    contact: null
  })

  const contactsQuery = useQuery(trpc.ops.contacts.list.queryOptions({}))
  const contacts = (contactsQuery.data ?? []).filter((contact) => matchesSearch(contact, search))

  return (
    <Flex direction="column" gap="4">
      <Flex align="center" justify="between" wrap="wrap" gap="3">
        <Heading size="6">Contacts</Heading>
        <Button onClick={() => setDialog({ open: true, contact: null })}>
          <PlusIcon />
          New contact
        </Button>
      </Flex>

      <Text size="2" color="gray">
        Contacts are shared across installations: editing one here updates every installation that references it.
      </Text>

      <TextField.Root
        placeholder="Search by name, email, phone, address or organization…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      >
        <TextField.Slot>
          <MagnifyingGlassIcon />
        </TextField.Slot>
      </TextField.Root>

      {contactsQuery.isLoading ? (
        <Text color="gray">Loading…</Text>
      ) : contactsQuery.error ? (
        <Text color="red">{contactsQuery.error.message}</Text>
      ) : contacts.length === 0 ? (
        <Text color="gray" size="2">
          No contacts found.
        </Text>
      ) : (
        <Table.Root variant="surface">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeaderCell>Name</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Email</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Phone</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Address</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Organization</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Installations</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell />
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {contacts.map((contact) => (
              <Table.Row key={contact.id}>
                <Table.Cell>
                  <Text size="2" weight="medium">
                    {contact.name}
                  </Text>
                </Table.Cell>
                <Table.Cell>{contact.email ?? '—'}</Table.Cell>
                <Table.Cell>{contact.phone ?? '—'}</Table.Cell>
                <Table.Cell>{contact.address ?? '—'}</Table.Cell>
                <Table.Cell>{contact.org?.name ?? '—'}</Table.Cell>
                <Table.Cell>
                  <Badge color="gray" variant="soft" size="1">
                    {contact.installationCount}
                  </Badge>
                </Table.Cell>
                <Table.Cell>
                  <Flex gap="2" justify="end">
                    <Tooltip content="Edit contact">
                      <IconButton
                        variant="soft"
                        color="gray"
                        size="1"
                        onClick={() => setDialog({ open: true, orgId: contact.org_id, contact })}
                      >
                        <Pencil1Icon />
                      </IconButton>
                    </Tooltip>
                    <DeleteContactButton contact={contact} />
                  </Flex>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      )}

      <ContactFormDialog
        open={dialog.open}
        onOpenChange={(open) => setDialog((prev) => ({ ...prev, open }))}
        orgId={dialog.orgId}
        contact={dialog.contact}
      />
    </Flex>
  )
}

export const Route = createFileRoute('/contacts/')({
  component: ContactsPage
})
